import moment, { type Moment } from "moment-timezone";
import { Clock } from "lucide-react";
import Tooltip from "../../../../../../components/shared/Tooltip";
import { useGridDates } from "../../../hooks/grid/useGridDates";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../../../../utils/hooks/company";
import { useShiftPreferenceStyle } from "../../../hooks/styles/useShiftPreferenceStyle";
import { renderDuration } from "../../../helpers/eventdisplay/renderDuration";
import { EVENT_ROW_HEIGHT, LANE_PADDING } from "../../../constants/height";

interface CreationData {
    resourceId: string;
    resourceIndex: number;
    laneIndex: number;
    startDate: Moment;
    endDate: Moment;
}

interface CreationPreviewProps {
    data: CreationData;
    rowTop: number;
    cellWidth: number;
}

const CreationPreview = ({ data, rowTop, cellWidth }: CreationPreviewProps) => {
    const { startDate, endDate, laneIndex } = data;
    const { gridStartTime } = useGridDates();
    const timeformat = useCompanyTimeFormat();
    const dateFormat = useCompanyDateFormat();
    const preferenceStyle = useShiftPreferenceStyle();

    const offsetMinutesFromGridStart = moment.duration(startDate.diff(gridStartTime)).asMinutes();
    const durationInMinutes = moment.duration(endDate.diff(startDate)).asMinutes();

    const leftOffsetPx = (offsetMinutesFromGridStart / 60) * cellWidth;
    const eventWidthPx = (durationInMinutes / 60) * cellWidth;

    if (eventWidthPx <= 0) return null;

    const topInRow = laneIndex * (EVENT_ROW_HEIGHT + LANE_PADDING) + LANE_PADDING;
    const finalTop = rowTop + topInRow;

    return (
        <Tooltip
            open={true}
            contentClassName="tw-bg-white tw-text-neutral-900 tw-shadow-sm"
            trigger={(
                <div
                    className="creation-preview tw-absolute tw-border-l-2 tw-rounded-sm z-5 tw-pointer-events-none tw-flex tw-items-center tw-justify-start border-box tw-px-2"
                    style={{
                        top: `${finalTop}px`,
                        height: `${EVENT_ROW_HEIGHT}px`,
                        left: `${leftOffsetPx}px`,
                        width: `${eventWidthPx}px`,
                        ...preferenceStyle,
                    }}
                >
                    <div className="tw-text-neutral-900 tw-p-2 tw-whitespace-nowrap tw-text-xs tw-overflow-hidden">
                        {startDate.format(timeformat)} - {endDate.format(timeformat)}
                        <span className="tw-ml-2 tw-font-semibold">({renderDuration(durationInMinutes * 60).trim()})</span>
                    </div>
                </div>
            )}
        >
            <div className="tw-flex tw-flex-col tw-gap-2 tw-p-2 tw-justify-start">
                <div className="tw-flex tw-gap-2 tw-items-center tw-text-sm tw-justify-start">
                    <Clock size={17} />
                    <h2>{moment(startDate).format(dateFormat + " " + timeformat)}</h2>
                </div>
                <div className="tw-flex tw-gap-2 tw-items-center tw-text-sm tw-justify-start">
                    <Clock size={17} />
                    <h2>{moment(endDate).format(dateFormat + " " + timeformat)}</h2>
                </div>
            </div>
        </Tooltip>
    );
};

export default CreationPreview;
